import { type FormEvent, useState } from 'react';
import { useApi, useConsulta } from '@plataforma/web/api';
import { fechaCorta } from '../formato';
import { usePuede } from '../permisos';

interface Historial {
  id: string;
  ordenId: string;
  vin: string;
  tipo: 'reparacion' | 'preparacion';
  fecha: string;
  codigo: string;
  odometro: number | null;
  unidadOdometro: 'km' | 'mi';
  registro: {
    zonas: { zona: string; nivel: string }[];
    piezas: { nombre: string }[];
    horas: string;
    tareas: { etapa: string; descripcion: string }[];
  };
  taller: string | null;
  fotos: string[];
  nota: string | null;
  publicado: boolean;
  publicadoEn: string | null;
}

/**
 * Historial de reparación verificable (arquitectura 2.15): cada orden cerrada deja un registro
 * (zonas, piezas, horas, taller) que el lotero publica en la ficha del auto con sello y QR;
 * solo lo publicable, nunca costos.
 */
export function HistorialReparacion({ vehiculoId }: { vehiculoId: string }) {
  const api = useApi();
  const permitido = usePuede();
  const lista = useConsulta<Historial[]>(`/workshop/historial?vehiculoId=${vehiculoId}`);
  const [abierto, setAbierto] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const puedePublicar = permitido('publicaciones.administrar');
  const publicar = async (h: Historial, e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const t = (k: string) => String(f.get(k) ?? '').trim();
    setError(null);
    try {
      await api('POST', `/workshop/historial/${h.id}/publicar`, {
        odometro: t('odometro') ? Number(t('odometro')) : null,
        unidadOdometro: t('unidadOdometro') || 'km',
        taller: t('taller') || null,
        fotos: t('fotos')
          .split('\n')
          .map((x) => x.trim())
          .filter(Boolean),
        nota: t('nota') || null,
      });
      setAbierto(null);
      lista.recargar();
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const despublicar = async (h: Historial) => {
    setError(null);
    try {
      await api('POST', `/workshop/historial/${h.id}/despublicar`);
      lista.recargar();
    } catch (err) {
      setError((err as Error).message);
    }
  };
  return (
    <section className="seccion">
      <header>
        <h2>Historial de reparación</h2>
        <span className="subtitulo">Sello y QR verificable en tu tienda y el marketplace</span>
      </header>
      {lista.error && (
        <p className="error" style={{ margin: 16 }}>
          {lista.error}
        </p>
      )}
      {error && (
        <p className="error" style={{ margin: 16 }}>
          {error}
        </p>
      )}
      {lista.datos && !lista.datos.length ? (
        <p className="vacio">Sin registros: se crean al cerrar una orden del Taller.</p>
      ) : (
        <div className="cuerpo" style={{ display: 'grid', gap: 12 }}>
          {(lista.datos ?? []).map((h) => (
            <article key={h.id} className="tarjeta" style={{ display: 'grid', gap: 6 }}>
              <div className="rotulo">
                {h.tipo === 'reparacion' ? 'Reparación' : 'Preparación'} · {fechaCorta(h.fecha)} ·
                código {h.codigo}
              </div>
              <div>
                {h.registro.zonas.length
                  ? `Zonas: ${h.registro.zonas.map((z) => z.zona).join(', ')}`
                  : 'Sin zonas registradas'}
                {h.registro.piezas.length
                  ? ` · Piezas: ${h.registro.piezas.map((p) => p.nombre).join(', ')}`
                  : ''}
                {Number(h.registro.horas) > 0 ? ` · ${h.registro.horas} h` : ''}
              </div>
              <div className="nota">
                {h.publicado ? (
                  <span className="etiqueta exito">Publicado</span>
                ) : (
                  <span className="etiqueta">Sin publicar</span>
                )}
                {h.taller ? ` · ${h.taller}` : ''}
                {h.odometro !== null
                  ? ` · ${h.odometro.toLocaleString('es-MX')} ${h.unidadOdometro}`
                  : ''}
              </div>
              {puedePublicar && (
                <div className="acciones">
                  <button
                    className="boton"
                    onClick={() => setAbierto(abierto === h.id ? null : h.id)}
                  >
                    {h.publicado ? 'Editar lo publicado' : 'Publicar con sello y QR'}
                  </button>
                  {h.publicado && (
                    <button className="boton" onClick={() => void despublicar(h)}>
                      Despublicar
                    </button>
                  )}
                </div>
              )}
              {abierto === h.id && (
                <form className="formulario" onSubmit={(e) => void publicar(h, e)}>
                  <label className="campo">
                    <span>Odómetro</span>
                    <input name="odometro" type="number" min={0} defaultValue={h.odometro ?? ''} />
                  </label>
                  <label className="campo">
                    <span>Unidad</span>
                    <select name="unidadOdometro" defaultValue={h.unidadOdometro}>
                      <option value="km">km</option>
                      <option value="mi">millas</option>
                    </select>
                  </label>
                  <label className="campo">
                    <span>Taller (como se publica)</span>
                    <input
                      name="taller"
                      maxLength={120}
                      defaultValue={h.taller ?? ''}
                      placeholder="Taller propio"
                    />
                  </label>
                  <label className="campo" style={{ gridColumn: '1 / -1' }}>
                    <span>Fotos de antes y después (una liga por renglón)</span>
                    <textarea name="fotos" rows={2} defaultValue={h.fotos.join('\n')} />
                  </label>
                  <label className="campo" style={{ gridColumn: '1 / -1' }}>
                    <span>Nota pública</span>
                    <input name="nota" maxLength={500} defaultValue={h.nota ?? ''} />
                  </label>
                  <div className="acciones">
                    <button className="boton primario">Publicar</button>
                  </div>
                </form>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
